import React, { useEffect, useMemo, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { apiService } from '../services/apiService';
import SearchableSelect from '../components/SearchableSelect';

const QUALIFICATIONS = [
  'Certificate in Graphic Design',
  'Certificate in Information Technology',
  'Certificate in Film Studies',
  'Diploma in Graphic Design',
  'Diploma in Information Technology',
  'Diploma in Film Production',
  'Advanced Diploma in Multimedia Design',
  'Bachelor of Arts in Film Studies',
  'Bachelor of Science in Information Technology',
  'Bachelor of Design (Graphic Design)',
  'Postgraduate Diploma in Education',
  'Master of Arts in Film & Media',
  'Master of Science in Computer Science',
  'PhD in Media Studies',
  'PGCE (Post Graduate Certificate in Education)',
];

const SPECIALISATIONS = [
  'Cinematography',
  'Video Editing',
  'Film Directing',
  'Screenwriting',
  'Advanced Film Production',
  'Documentary Filmmaking',
  'Motion Graphics',
  'Animation',
  'Graphic Design',
  'UI/UX Design',
  'Branding & Identity',
  'Digital Illustration',
  'Print Design',
  'Photography',
  'Multimedia Design',
  'Web Development',
  'Software Engineering',
  'Networking & Systems',
  'Cybersecurity',
  'Database Administration',
  'Mobile App Development',
  'Digital Marketing',
  'E-Commerce',
  'Cloud Computing',
];

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  subject: '',
  qualification: '',
  phone: '',
  address: '',
  joinDate: '',
  active: true,
};

const Teachers = () => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadTeachers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getAll('teachers');
      setTeachers(data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load staff.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeachers();
  }, []);

  const filteredTeachers = useMemo(
    () =>
      teachers.filter(teacher =>
        [
          teacher.firstName,
          teacher.lastName,
          teacher.email,
          teacher.subject,
          teacher.qualification,
        ]
          .join(' ')
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [search, teachers]
  );

  const openAdd = () => {
    setCurrent(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEdit = teacher => {
    setCurrent(teacher);
    setForm({
      firstName: teacher.firstName || '',
      lastName: teacher.lastName || '',
      email: teacher.email || '',
      password: '',
      subject: teacher.subject || '',
      qualification: teacher.qualification || '',
      phone: teacher.phone || '',
      address: teacher.address || '',
      joinDate: teacher.joinDate || '',
      active: teacher.active ?? true,
    });
    setIsModalOpen(true);
  };

  const handleChange = event => {
    const { name, value, type, checked } = event.target;
    setForm(previous => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      if (current) {
        await apiService.update('teachers', current._id, form);
      } else {
        await apiService.create('teachers', form);
      }
      setIsModalOpen(false);
      setForm(emptyForm);
      await loadTeachers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save staff member.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this staff member?')) return;
    try {
      await apiService.delete('teachers', id);
      await loadTeachers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete staff member.');
    }
  };

  if (loading) return <LoadingSpinner message="Loading staff..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Staff</h1>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Staff</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search by name, email or specialisation..."
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
      </div>

      <div className="table-responsive">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Specialisation</th>
              <th>Qualification</th>
              <th>Phone</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTeachers.length === 0 ? (
              <tr><td colSpan={6} className="no-data-message">No staff found.</td></tr>
            ) : (
              filteredTeachers.map(teacher => (
                <tr key={teacher._id}>
                  <td>{teacher.firstName} {teacher.lastName}</td>
                  <td>{teacher.email}</td>
                  <td>{teacher.subject || 'Not set'}</td>
                  <td>{teacher.qualification}</td>
                  <td>{teacher.phone || 'N/A'}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" onClick={() => openEdit(teacher)} title="Edit">Edit</button>
                      <button className="btn-icon" onClick={() => handleDelete(teacher._id)} title="Delete">Delete</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={current ? 'Edit Staff' : 'Add Staff'}>
        <form onSubmit={handleSubmit} className="form">
          <div className="form-row">
            <div className="form-group">
              <label>First Name</label>
              <input name="firstName" value={form.firstName} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input name="lastName" value={form.lastName} onChange={handleChange} required />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                required={!current}
                placeholder={current ? 'Leave blank to keep existing password' : ''}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Specialisation</label>
              <SearchableSelect
                name="subject"
                value={form.subject}
                onChange={handleChange}
                options={SPECIALISATIONS}
                placeholder="Search specialisation..."
              />
            </div>
            <div className="form-group">
              <label>Qualification</label>
              <SearchableSelect
                name="qualification"
                value={form.qualification}
                onChange={handleChange}
                options={QUALIFICATIONS}
                placeholder="Search qualification..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Phone</label>
              <input name="phone" value={form.phone} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Join Date</label>
              <input type="date" name="joinDate" value={form.joinDate} onChange={handleChange} required />
            </div>
          </div>

          <div className="form-group">
            <label>Address</label>
            <input name="address" value={form.address} onChange={handleChange} />
          </div>

          <div className="form-group" style={{ justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" name="active" id="teacher-active" checked={form.active} onChange={handleChange} />
            <label htmlFor="teacher-active" style={{ marginBottom: 0 }}>Active</label>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : current ? 'Update Staff' : 'Add Staff'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Teachers;
